// Shared types used by both the React frontend and the Cloudflare Worker.

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'user' | 'admin';
  kyc_status: 'none' | 'pending' | 'verified' | 'rejected';
  balance: number;
  created_at: string;
}

export interface Plan {
  id: string;
  name: string;
  tier_level: number;
  min_investment: number;
  max_investment: number;
  duration_months: number;
  expected_roi_monthly: number;
  profit_multiplier: number;
  risk_level: 'low' | 'medium' | 'high' | 'very_high';
  category: string;
  ai_powered: boolean;
  gambling_access: boolean;
  prediction_access: boolean;
  is_active: boolean;
  is_recommended: boolean;
  logo_url?: string;
  color_badge?: string;
  features: string[];
  created_at?: string;
}

export interface Asset {
  id: string;
  symbol: string;
  name: string;
  type: 'stock' | 'crypto' | 'forex' | 'futures' | 'index';
  exchange?: string;
  sector?: string;
  current_price: number;
  price_change_24h: number;
  price_change_percent_24h: number;
  high_24h?: number;
  low_24h?: number;
  volume_24h?: number;
  market_cap?: number;
  icon_color?: string;
  icon_url?: string;
  sparkline_data: number[];
  description?: string;
  last_updated?: string;
  created_at?: string;
}

export interface Holding {
  id: string;
  user_id: string;
  asset_id: string;
  quantity: number;
  avg_cost: number;
  created_at?: string;
  updated_at?: string;
}

export interface Order {
  id: string;
  user_id: string;
  kind?: 'receipt' | 'challenge';
  challenge_name?: string;
  challenge_type?: 'Beginner' | 'Mid-Level' | 'Professional';
  purchase_type?: 'monthly' | 'activation' | 'reset';
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  description?: string;
  created_at?: string;
  // Legacy fields (kept for back-compat with the original Base44-SDK bundle)
  asset_id?: string;
  order_type?: 'buy' | 'sell';
  quantity?: number;
  price?: number;
  plan_id?: string;
  filled_at?: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  kind: 'challenge' | 'market_data';
  plan_name?: string;
  plan_id?: string;
  market_data_name?: string;
  status: 'active' | 'cancelled' | 'expired';
  expires_at?: string;
  started_at?: string;
  created_at?: string;
}

export interface Platform {
  id: string;
  name: string;
  description?: string;
  requires_subscription?: 'challenge' | 'market_data' | 'both';
  logo_url?: string;
  created_at?: string;
}

export interface EconomicCalendarEvent {
  id: string;
  event_date: string;
  country: string;
  currency?: string;
  description: string;
  impact: 'low' | 'medium' | 'high';
  restricted: 0 | 1;
  actual?: string;
  forecast?: string;
  previous?: string;
  created_at?: string;
}

export interface Payout {
  id: string;
  user_id: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  method?: 'bank' | 'wire' | 'usdt';
  requested_at?: string;
  paid_at?: string;
  reference?: string;
  created_at?: string;
}

export interface ChallengeProgress {
  id: string;
  user_id: string;
  challenge_id?: string;
  progress_date: string;
  account_balance: number;
  daily_pnl: number;
  total_pnl_pct: number;
  standing: 0 | 1;
  created_at?: string;
}

export interface AffiliateSignup {
  id: string;
  user_id?: string;
  email: string;
  full_name?: string;
  discount_coupon?: string;
  aff_code: string;
  social_link?: string;
  info?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at?: string;
}

export interface ChallengeTier {
  id: string;
  tier_name: 'Beginner' | 'Mid' | 'Professional';
  size: number;
  price: number;
  zc_drawdown_mode?: string;
  zc_max_drawdown?: number;
  zc_profit_target?: number;
  zc_consistency_pct?: number;
  zc_reset_price?: number;
  zf_drawdown_mode?: string;
  zf_max_drawdown?: number;
  zf_daily_drawdown_soft?: number;
  zf_profit_share?: number;
  zf_minimum_profits?: number;
  zf_sessions?: string;
  zf_consistency_pct?: number;
  zf_reset_price?: number;
  zf_activation_fee?: string;
  monthly_fee_included?: number;
  created_at?: string;
}

export interface TradingPlatform {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  market_data?: string;
  license_included?: number;
  features?: string[];
  screenshot_url?: string;
  web_url?: string;
  created_at?: string;
}

export interface ActiveChallenge {
  id: string;
  user_id: string;
  tier_name: 'Beginner' | 'Mid' | 'Professional';
  size: number;
  challenge_type: 'Zero Challenge' | 'Zero Funded';
  status?: 'active' | 'passed' | 'failed';
  started_at?: string;
  expires_at?: string;
  account_balance?: number;
  created_at?: string;
}

export interface UserPlatform {
  id: string;
  user_id: string;
  platform_id: string;
  status?: 'active' | 'inactive';
  selected_at?: string;
  created_at?: string;
}

export interface Transaction {
  id: string;
  user_id: string;
  tx_type: 'deposit' | 'withdrawal' | 'investment' | 'return' | 'plan_join';
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  reference_id?: string;
  description?: string;
  created_at?: string;
}

export interface AISignal {
  id: string;
  asset_id?: string;
  signal_type: 'buy' | 'sell' | 'hold';
  confidence: number;
  rationale?: string;
  target_price?: number;
  stop_loss?: number;
  created_at?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  user: User;
}

export interface ApiError {
  error_type: string;
  message: string;
  detail?: string;
  code?: string;
  traceback?: string;
  request_id?: string | null;
}

export const APP_ID = 'funderpro-live';
export const WORKER_BASE_URL = 'https://funderpro-investment.rustybm.workers.dev';

export const isApiError = (x: unknown): x is ApiError =>
  !!x && typeof x === 'object' && 'error_type' in (x as Record<string, unknown>);

export const entityPath = (name: string, id?: string) => {
  const base = `/api/apps/${APP_ID}/entities/${name}`;
  return id ? `${base}/${id}` : base;
};
export const authPath = (action: 'login' | 'register' | 'me' | 'verify-otp' | 'resend-otp' | 'reset-password-request' | 'reset-password' | 'change-password') =>
  `/api/apps/${APP_ID}/auth/${action}`;
export const aiPath = () => `/api/apps/${APP_ID}/ai/openai/v1/chat/completions`;
